import type { AdminResponse, AuthUser, CapturesResponse, CaptureStaffResponse, ChatResponse, FunnelResponse, InsightsResponse, LoginResult, OnlineConvRow, OnlineRawConversationResponse, OnlineResponse, OpdResponse, OverviewResponse, ProductDemandResponse, ProductDemandRow, ProductResolutionQcRow, StatusResponse } from './types';
import { DEFAULT_RANGE } from './ranges';

const AUTH_TOKEN_KEY = 'care_cdp_gauth_access_token';
const AUTH_REFRESH_KEY = 'care_cdp_gauth_refresh_token';
const AUTH_EXPIRES_KEY = 'care_cdp_gauth_expires_at';

export function getAuthToken(): string {
  // Access tokens are now kept server-side in an HttpOnly same-origin cookie.
  return '';
}

export function clearAuthTokens(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_REFRESH_KEY);
  localStorage.removeItem(AUTH_EXPIRES_KEY);
}

export function isAuthExpired(): boolean {
  return false;
}

export function isLocalDevHost(): boolean {
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

export function localDevToken(): string {
  return isLocalDevHost() ? '__local_dev__' : '';
}

export function displayUserName(user: AuthUser | null): string {
  if (!user) return 'G-Auth';
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return user.name || fullName || user.username || user.email || 'G-Auth user';
}

function authHeader(token = getAuthToken()): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function authMessage(payload: Record<string, unknown>, fallback: string): string {
  return String(payload.message || payload.error || payload.raw || fallback);
}

async function readJson(response: Response): Promise<Record<string, unknown>> {
  return response.json().catch(() => ({}));
}

// Abort-timeout wrapper so a stalled request never hangs the UI (the same no-hang guarantee
// the backend G-Auth proxy enforces, applied client-side for edge/network stalls).
async function fetchWithTimeout(input: RequestInfo, init: RequestInit = {}, ms = 12000): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(input, { credentials: 'same-origin', ...init, signal: controller.signal });
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw new Error('Kết nối quá thời gian chờ. Vui lòng thử lại.');
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export async function loginWithGAuth(username: string, password: string): Promise<LoginResult> {
  const response = await fetchWithTimeout('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const payload = await readJson(response);
  if (!response.ok) throw new Error(authMessage(payload, 'Đăng nhập thất bại.'));
  const data = (payload.data || payload) as Record<string, unknown>;
  clearAuthTokens();
  return { token: '', user: (data.user || payload.user || data || {}) as AuthUser };
}

export async function getCurrentUser(): Promise<AuthUser> {
  const response = await fetchWithTimeout('/api/auth/me');
  const payload = await readJson(response);
  if (!response.ok) {
    if (response.status === 401) clearAuthTokens();
    // carry the status so callers can tell a genuine 401 (re-login) from a transient
    // 504/upstream timeout (keep the session, degrade gracefully).
    const error = new Error(authMessage(payload, 'Phiên đăng nhập không hợp lệ.')) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  const data = payload.data as Record<string, unknown> | undefined;
  return (data?.user || data || payload.user || {}) as AuthUser;
}

export async function logoutFromGAuth(): Promise<void> {
  await fetchWithTimeout('/api/auth/logout', { method: 'POST' }, 5000).catch(() => undefined);
  clearAuthTokens();
}

async function apiGet<T>(path: string, tokenOverride = ''): Promise<T> {
  const token = tokenOverride || localDevToken();
  const response = await fetchWithTimeout(path, { headers: authHeader(token) });
  const payload = await readJson(response);
  if (!response.ok) {
    const error = new Error(authMessage(payload, `HTTP ${response.status}`)) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  return payload as T;
}

export const loadOverview = (token = ''): Promise<OverviewResponse> => apiGet('/api/care', token);
async function loadCapturesPage(offset: number, limit: number, token = ''): Promise<CapturesResponse> {
  return apiGet(`/api/care/captures?offset=${offset}&limit=${limit}`, token);
}

export async function loadCaptures(token = ''): Promise<CapturesResponse> {
  const limit = 1000;
  const details: CapturesResponse['details'] = [];
  let offset = 0;
  let lastPage: CapturesResponse | null = null;

  for (let guard = 0; guard < 20; guard++) {
    const page = await loadCapturesPage(offset, limit, token);
    const pageDetails = page.details || [];
    details.push(...pageDetails);
    lastPage = page;
    if (pageDetails.length < limit) break;
    offset += limit;
  }

  return {
    offset: 0,
    limit,
    count: details.length,
    has_more: Boolean(lastPage?.has_more && (lastPage.details || []).length === limit),
    details,
  };
}
export const loadChat = (token = ''): Promise<ChatResponse> => apiGet('/api/care/chat', token);
export const loadFunnel = (token = ''): Promise<FunnelResponse> => apiGet('/api/care/funnel', token);
export async function loadInsights(token = '', range = DEFAULT_RANGE): Promise<InsightsResponse> {
  // Insights returns the full enriched history and can take longer than the
  // default API timeout while AI Hub serializes and streams the response.
  const response = await fetchWithTimeout('/api/care/insights?range=' + encodeURIComponent(range), {
    headers: authHeader(token || localDevToken()),
  }, 45000);
  const payload = await readJson(response);
  if (!response.ok) throwApiError(response, payload);
  return payload as unknown as InsightsResponse;
}
export const loadStatus = (token = ''): Promise<StatusResponse> => apiGet('/api/care/status', token);
export const loadStaff = (token = '', range = DEFAULT_RANGE): Promise<CaptureStaffResponse> => apiGet('/api/care/staff?range=' + encodeURIComponent(range), token);
export const loadAdmin = (token = '', range = DEFAULT_RANGE): Promise<AdminResponse> => apiGet('/api/care/admin?range=' + encodeURIComponent(range), token);
export const loadOpd = (token = '', range = '30d'): Promise<OpdResponse> => apiGet('/api/care/opd?range=' + encodeURIComponent(range), token);
const ONLINE_FILTER_PARAMS: Record<string, string> = {
  channels: 'channel',
  categories: 'category',
  subcats: 'subcategory',
  stages: 'deal_stage',
  salesStages: 'sales_stage',
  intents: 'intent',
  blockers: 'blocker',
  priorities: 'priority',
  complaints: 'complaint',
  groups: 'group',
};

export async function loadOnline(token = '', range = DEFAULT_RANGE, filters: Record<string, string[]> = {}): Promise<OnlineResponse> {
  const query = onlineQuery(range, filters);
  const headers = authHeader(token || localDevToken());
  const [summaryResponse, detailsResponse] = await Promise.all([
    fetchWithTimeout('/api/care/online/summary?' + query, { headers }, 45000),
    fetchWithTimeout('/api/care/online/conversations?' + query + '&limit=250&offset=0', { headers }, 45000),
  ]);
  const summary = await readJson(summaryResponse) as OnlineResponse & { meta?: Record<string, unknown> };
  const details = await readJson(detailsResponse) as Record<string, unknown>;
  if (!summaryResponse.ok) throwApiError(summaryResponse, summary as unknown as Record<string, unknown>);
  if (!detailsResponse.ok) throwApiError(detailsResponse, details);
  const conversations = ((details.conversations as unknown[]) || []).map(row => expandOnlineRow(row as Record<string, unknown>));
  return {
    ...summary,
    meta: {
      ...summary.meta,
      detail_total: Number(details.total || 0),
      detail_limit: 250,
      detail_has_more: Boolean(details.has_more),
    },
    conversations,
  };
}

export function loadOnlineConversationRaw(conversationId: string, token = ''): Promise<OnlineRawConversationResponse> {
  return apiGet('/api/care/online/raw?conversation_id=' + encodeURIComponent(conversationId), token);
}

function onlineQuery(range: string, filters: Record<string, string[]>): string {
  const params = new URLSearchParams({ range });
  Object.entries(ONLINE_FILTER_PARAMS).forEach(([key, param]) => {
    const values = (filters[key] || []).filter(Boolean);
    if (values.length) params.set(param, values.join(','));
  });
  return params.toString();
}

function throwApiError(response: Response, payload: Record<string, unknown>): never {
  const error = new Error(authMessage(payload, `HTTP ${response.status}`)) as Error & { status?: number };
  error.status = response.status;
  throw error;
}

function expandOnlineRow(row: Record<string, unknown>): OnlineConvRow {
  if ('conversation_id' in row) return row as unknown as OnlineConvRow;
  return {
    conversation_id: String(row.id || ''),
    channel: row.ch as string | null,
    group_name: row.grp as string | null,
    agent_name: row.ag as string | null,
    has_phone: Boolean(row.hp),
    sales_stage: row.ss as string | null,
    ai_category: row.cat as string | null,
    ai_subcategory_name: row.sub as string | null,
    ai_need_type: row.nt as string | null,
    ai_deal_stage: row.ds as string | null,
    ai_blocker_type: row.blk as string | null,
    ai_purchase_intent: row.intent as string | null,
    ai_value_tier: row.tier as string | null,
    high_value_flag: Boolean(row.hv),
    ai_followup_priority: row.prio as string | null,
    complaint_level: row.comp as string | null,
    ai_need_summary: row.sum as string | null,
    ai_next_best_action: row.next as string | null,
    ai_products: expandProducts(row.prod, row.cat as string | null),
    day: row.day as string | null,
  };
}

function expandProducts(value: unknown, category: string | null): OnlineConvRow['ai_products'] {
  if (!Array.isArray(value)) return [];
  return value
    .map(item => {
      if (typeof item === 'string') return { name: item, category };
      if (item && typeof item === 'object') return item as { name?: string; brand?: string | null; category?: string | null };
      return null;
    })
    .filter((item): item is { name?: string; brand?: string | null; category?: string | null } => Boolean(item?.name));
}

export async function loadProductDemand(token = ''): Promise<ProductDemandResponse> {
  const raw = await apiGet<{ meta?: ProductDemandResponse['meta']; rows?: unknown[]; qc?: unknown[] }>('/api/care/product-demand', token);
  const rows = (raw.rows || []).map(normalizeDemandRow);
  const qc = (raw.qc || []) as ProductResolutionQcRow[];
  return { meta: raw.meta, rows, qc };
}

function normalizeDemandRow(value: unknown): ProductDemandRow {
  const r = (value || {}) as Record<string, unknown>;
  const b = (v: unknown) => v === true || v === 't' || v === 1;
  return {
    capture_id: String(r.id ?? ''),
    mention_idx: Number(r.ix ?? 0),
    raw_name: String(r.rn ?? ''),
    tier: (r.t as ProductDemandRow['tier']) ?? 'generic',
    canonical_key: String(r.ck ?? ''),
    canonical_name: String(r.cn ?? r.rn ?? ''),
    brand_final: (r.br as string) ?? null,
    brand_corrected: b(r.bc),
    group_code: (r.g as string) ?? null,
    subcat_name: (r.sc as string) ?? null,
    catalog_product_id: r.pid != null ? Number(r.pid) : null,
    match_method: String(r.mm ?? ''),
    match_score: Number(r.ms ?? 0),
    price_segment: (r.seg as string) ?? null,
    condition_demand: (r.cond as string) ?? null,
    is_resolved: b(r.res),
    store_code: (r.store as string) ?? null,
    day: (r.day as string) ?? null,
    ai_category: (r.cat as string) ?? null,
    ai_deal_stage: (r.stage as string) ?? null,
    ai_purchase_intent: (r.intent as string) ?? null,
    high_value_flag: b(r.hv),
    stock_gap: b(r.sg),
    demo_gap: b(r.dg),
    closed_won: b(r.won),
  };
}

export interface OnlineDemandView {
  meta?: { live?: boolean; refreshed_at?: string; range?: string; source?: string };
  error?: string;
  kpis?: {
    mentions: number; conversations: number; groups: number; resolved: number; usable: number;
    normalized: number; category_only: number; unmapped: number; order_linked: number;
    brand_fixed: number; generic_mentions: number; raw_names: number;
  };
  trend?: Array<{ period: string; n: number; resolved: number; stock: number; hv: number }>;
  dow?: Array<{ dow: number; n: number }>;
  cattrend?: Array<{ group_code: string; period: string; n: number }>;
  groups?: Array<{ group_code: string; n: number }>;
  channels?: Array<{ channel: string; n: number }>;
  brands?: Array<{ brand: string; n: number }>;
  sku?: Array<Record<string, unknown>>;
  series?: Array<Record<string, unknown>>;
  generic?: Array<Record<string, unknown>>;
  momentum?: Array<{ name: string; brand: string | null; group_code: string | null; recent: number; prior: number }>;
  spike?: Array<{ name: string; brand: string | null; group_code: string | null; latest: number; baseline: number; ratio: number }>;
  span?: { lo: string; hi: string } | null;
}

export async function loadOnlineProductDemand(token = '', range = DEFAULT_RANGE, gran = 'day', filters: Record<string, string[]> = {}): Promise<OnlineDemandView> {
  const qs = new URLSearchParams({ range, gran });
  for (const [k, v] of Object.entries(filters)) if (v && v.length) qs.set(k, v.join(','));
  // server-side aggregation over a large exploded set — use the ONLINE long timeout (45s), not 12s.
  const response = await fetchWithTimeout('/api/care/online/product-demand?' + qs.toString(),
    { headers: authHeader(token || localDevToken()) }, 45000);
  const payload = await readJson(response) as OnlineDemandView;
  if (!response.ok) {
    const error = new Error(authMessage(payload as Record<string, unknown>, `HTTP ${response.status}`)) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  return payload;
}

// ═══════════════ Ops Quality (Chất lượng vận hành — nguồn ops-quality D1 qua AI Hub) ═══════════════

import type { OpsComplaintsResponse, OpsQualityResponse, OpsReviewsResponse, OpsTcdtResponse, OpsWarrantyResponse } from './types';

const opsPath = (endpoint: string, month: string): string =>
  `/api/care/${endpoint}${month ? `?month=${encodeURIComponent(month)}` : ''}`;

export const loadOpsQuality = (token = '', month = ''): Promise<OpsQualityResponse> =>
  apiGet(opsPath('ops-quality', month), token);
export const loadOpsComplaints = (token = '', month = ''): Promise<OpsComplaintsResponse> =>
  apiGet(opsPath('ops-complaints', month), token);
export const loadOpsWarranty = (token = '', month = ''): Promise<OpsWarrantyResponse> =>
  apiGet(opsPath('ops-warranty', month), token);
export const loadOpsReviews = (token = '', month = ''): Promise<OpsReviewsResponse> =>
  apiGet(opsPath('ops-reviews', month), token);
export const loadOpsTcdt = (token = '', month = ''): Promise<OpsTcdtResponse> =>
  apiGet(opsPath('ops-tcdt', month), token);

import type { OpsSummaryResponse } from './types';

export const loadOpsSummary = (token = ''): Promise<OpsSummaryResponse> =>
  apiGet('/api/care/ops-summary', token);

// ═══════════════ OPD raw (thảo luận vận hành Lark, schema `opd`) ═══════════════

import type { OpdRawResponse } from './types';

export const loadOpdRaw = (token = '', range = '30d'): Promise<OpdRawResponse> =>
  apiGet(`/api/care/opd-raw?range=${encodeURIComponent(range)}`, token);
